import { Button, Checkbox, Divider, Group, Input, Modal, MultiSelect, Stack, Text, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useEffect } from 'react';
import { ObjectArrayType } from '../routes/customers/customer_kyc';

const PepModificationModal = ({ opened, onClose, onSubmit, requireEditReason, politicalPositionOptions, initialPepPoliticalPositions = [], initialRelativePoliticalPositions = [], initialRelativePoliticalPositionInformation = [] }: {
    opened: boolean,
    requireEditReason: boolean,
    politicalPositionOptions: ObjectArrayType[],
    initialPepPoliticalPositions?: string[],
    initialRelativePoliticalPositions?: string[],
    initialRelativePoliticalPositionInformation?: { relation: string, name: string }[],
    onClose: () => void,
    onSubmit: (value: { includePep: boolean, includeRelative: boolean, pepPoliticalPositions: string[], relativePoliticalPositions: string[], relativeRelation?: string, relativeName?: string, editReason?: string }) => any,
}) => {
    const pepMatchForm = useForm<{ includePep: boolean, includeRelative: boolean, pepPoliticalPositions: string[], relativePoliticalPositions: string[], relativeRelation?: string, relativeName?: string, editReason?: string }>({
        initialValues: {
            includePep: false,
            includeRelative: false,
            pepPoliticalPositions: [],
            relativePoliticalPositions: [],
            relativeRelation: "",
            relativeName: "",
        },
        validate: {
            pepPoliticalPositions: (val, values) => (values.includePep && (!val || val.length === 0) ? "Please select at least one PEP political position" : null),
            relativePoliticalPositions: (val, values) => (values.includeRelative && (!val || val.length === 0) ? "Please select at least one relative political position" : null),
            relativeRelation: (val, values) => (values.includeRelative && (!val || !val.trim()) ? "Relation is required for Relative of PEP" : null),
            relativeName: (val, values) => (values.includeRelative && (!val || !val.trim()) ? "Name is required for Relative of PEP" : null),
            editReason: (val) => {
                if (requireEditReason && (!val || !val.length)) {
                    return "edit reason is required";
                }
                return null;
            },
        },
    });

    useEffect(() => {
        if (!opened) return;
        pepMatchForm.setValues({
            includePep: initialPepPoliticalPositions.length > 0,
            includeRelative: initialRelativePoliticalPositions.length > 0,
            pepPoliticalPositions: initialPepPoliticalPositions,
            relativePoliticalPositions: initialRelativePoliticalPositions,
            relativeRelation: initialRelativePoliticalPositionInformation?.[0]?.relation || "",
            relativeName: initialRelativePoliticalPositionInformation?.[0]?.name || "",
            editReason: "",
        });
    }, [opened, initialPepPoliticalPositions, initialRelativePoliticalPositions, initialRelativePoliticalPositionInformation]);

    return (
        <Modal
            opened={!!opened}
            onClose={() => {
                pepMatchForm.reset();
                onClose();
            }}
            title={<Title order={3}>Client Political Position</Title>}
        >
            <form
                onSubmit={pepMatchForm.onSubmit(async (values) => {
                    const response = await onSubmit(values);
                    if (response.data.status === "success") {
                        pepMatchForm.reset();
                        onClose();
                    }
                })}
            >
                <Stack>
                    <Text>Select</Text>

                    <Checkbox
                        label="PEP"
                        checked={pepMatchForm.values.includePep}
                        onChange={(e) => {
                            const checked = e.currentTarget.checked;
                            pepMatchForm.setFieldValue('includePep', checked);
                            if (!checked) pepMatchForm.setFieldValue('pepPoliticalPositions', []);
                        }}
                    />
                    {pepMatchForm.values.includePep && (
                        <MultiSelect
                            label="PEP Political Position(s)"
                            data={politicalPositionOptions}
                            value={pepMatchForm.values.pepPoliticalPositions}
                            error={pepMatchForm.errors.pepPoliticalPositions}
                            onChange={(value) => pepMatchForm.setFieldValue('pepPoliticalPositions', value)}
                        />
                    )}
                    <Divider h={"md"} />
                    <Checkbox
                        label="Relative of PEP"
                        checked={pepMatchForm.values.includeRelative}
                        onChange={(e) => {
                            const checked = e.currentTarget.checked;
                            pepMatchForm.setFieldValue('includeRelative', checked);
                            if (!checked) {
                                pepMatchForm.setFieldValue('relativePoliticalPositions', []);
                                pepMatchForm.setFieldValue('relativeRelation', "");
                                pepMatchForm.setFieldValue('relativeName', "");
                            }
                        }}
                    />
                    {pepMatchForm.values.includeRelative && (
                        <>
                            <MultiSelect
                                label="Relative Political Position(s)"
                                data={politicalPositionOptions}
                                value={pepMatchForm.values.relativePoliticalPositions}
                                error={pepMatchForm.errors.relativePoliticalPositions}
                                onChange={(value) => pepMatchForm.setFieldValue('relativePoliticalPositions', value)}
                            />
                            <TextInput
                                label="Relative Relation"
                                value={pepMatchForm.values.relativeRelation}
                                error={pepMatchForm.errors.relativeRelation}
                                onChange={(e) => pepMatchForm.setFieldValue('relativeRelation', e.currentTarget.value)}
                            />
                            <TextInput
                                label="Relative Name"
                                value={pepMatchForm.values.relativeName}
                                error={pepMatchForm.errors.relativeName}
                                onChange={(e) => pepMatchForm.setFieldValue('relativeName', e.currentTarget.value)}
                            />
                        </>
                    )}
                    <Divider h={"md"} />

                    {!!requireEditReason && <TextInput label="Reason"  {...pepMatchForm.getInputProps("editReason")} />}
                    <Divider h={"md"} />

                    <Group>
                        <Button color={"green"} type="submit">
                            Confirm
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Modal >
    );
};

export default PepModificationModal;
